import React, { useState } from 'react';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { useNavigate } from 'react-router-dom';
import { AuthState, confirmCode, getUserAccess } from '../AuthState';
import { AuthStep, FormSubmitHandler, type CodeFormData } from '../types';
import Swal from 'sweetalert2';
import { useAuthStep } from '../context/AuthStepContext';

const usePhoneVerification = (onSubmit?: (data: CodeFormData) => void) => {
  const navigate = useNavigate();
  const { resetStep, skipTo, nextStep, setIsSignInFlow, setHasAccount } =
    useAuthStep();
  const codeRef = React.useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const phone = AuthState.phone;
  const [codeValue, setCodeValue] = useState<string>('');

  const formatPhone = (p: string | null): string => {
    if (p && p.length === 12) {
      return `${p.substring(0, 3)} ${p.substring(3, 6)} ${p.substring(6, 9)} ${p.substring(9, 12)}`;
    } else {
      return p || '';
    }
  };

  const codeUpdated = async (phoneCode: string) => {
    setCodeValue(phoneCode);
    if (
      phoneCode.length === 4 &&
      (AuthState.emailCode?.length === 6 ||
        (AuthState.pin && AuthState.login_type === 'PIN'))
    ) {
      setIsLoading(true);
      let code: string;
      if (AuthState.pin && AuthState.login_type === 'PIN') {
        code = phoneCode.toString();
      } else {
        code = AuthState.emailCode!.toString() + phoneCode.toString();
      }
      const user: CognitoUser = await confirmCode(code);
      if (user && user.getSignInUserSession()) {
        const userAccess = await getUserAccess();
        if (!userAccess.registered) {
          setCodeValue('');
          nextStep();
        } else {
          if (userAccess.userStatus === 1) {
            window.location.href = '/';
            return;
          } else {
            setIsLoading(false);
            setIsSignInFlow(false);
            setHasAccount(true);
            skipTo(AuthStep.COMPLETE);
          }
        }
      } else if (user && !user.getSignInUserSession()) {
        const Toast = Swal.mixin({
          toast: true,
          position: 'bottom',
          timer: 3000,
          timerProgressBar: true,
        });
        Toast.fire(`Incorrect code. ${1} more retries`, '', 'info');
        setCodeValue('');
      } else {
        Swal.fire({
          icon: 'error',
          text: 'Maximum tries exceeded or code timed out. Please try again.',
          showConfirmButton: true,
        });
        setCodeValue('');
        resetStep();
      }
    }
  };

  const handleSubmit: FormSubmitHandler = (e) => {
    setIsLoading(true);
    e.preventDefault();
    // Additional submit logic can be added here if needed
    const data = {
      phone: codeRef.current?.value || '',
    };
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleBackClick = () => {
    navigate('/login');
    setCodeValue('');
    resetStep();
  };

  const checkPhoneAndRedirect = () => {
    if (!phone) {
      // TODO use a better solution than below. Using navigate doesn't work as the authentication doesn't update
      //navigate("/");
      window.location.href = window.location.hostname;
    }
  };

  return {
    phone,
    isLoading,
    formatPhone,
    codeUpdated,
    handleSubmit,
    handleBackClick,
    checkPhoneAndRedirect,
    navigate,
    codeValue,
  };
};

export { usePhoneVerification };
